import { useEffect, useMemo, useCallback } from 'react';
import { useLocalStorage } from '@hotjar/insights-common/hooks/useStorage';
import { sanitizeUUID } from '@hotjar/webapp/lib/sanitize';

import { useSearchParams } from 'react-router-dom';
import type { SortState } from '../components/Surveys/SurveyView/SurveyResponsesView/ResponsesList/ResponsesList';
import type { SurveyQuestion } from '../domain/Surveys/Questions';
import {
  getSearchParamToSortState,
  getServerSortField,
} from '../components/Surveys/utils/sortFieldsConverters';

import { DEFAULT_SORT_STATE, useSurveyViewStore } from '../domain/Surveys/surveyViewStore';

const SURVEY_SORTING_STORAGE_KEY = 'surveySorting';
interface UseSurveySort {
  sort: SortState;
  setSort: (newSort: SortState) => void;
  serverSortField: string;
}

const DEFAULT_SORTING_KEYS = [
  'index',
  'response_url',
  'created_epoch_time',
  'device',
  'location_id',
  'browser',
  'visitor_uuid',
  'os',
];

const questionExists = (questions: SurveyQuestion[] | undefined, sortValue: string) =>
  questions !== undefined && questions.some((q) => sanitizeUUID(q.uuid) === sortValue.slice(-8));

const updateSearchParams = (newSort: string) => {
  const params = new URLSearchParams(window.location.search);
  params.set('sort_by', newSort);
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}?${params.toString()}`.replaceAll('%3A', ':'),
  );
};

export const useSurveySorting = (questions?: SurveyQuestion[]): UseSurveySort => {
  const [storageSorting, setLocalStorageSort] = useLocalStorage(SURVEY_SORTING_STORAGE_KEY, '');
  const [searchParams] = useSearchParams();
  const { sort, setSorting } = useSurveyViewStore();

  const getDefaultSorting = () => {
    const sortBySearchParam = searchParams.get('sort_by');
    let sortValue: SortState | undefined;

    if (sortBySearchParam !== null && sortBySearchParam.length) {
      sortValue = getSearchParamToSortState(sortBySearchParam);
    } else if (storageSorting) {
      sortValue = getSearchParamToSortState(storageSorting);
    }

    if (
      sortValue &&
      (DEFAULT_SORTING_KEYS.includes(sortValue?.key) || questionExists(questions, sortValue?.key))
    ) {
      return sortValue;
    }

    return DEFAULT_SORT_STATE;
  };

  useEffect(() => {
    if (questions !== undefined) {
      const defaultSort = getDefaultSorting();
      const paramSort = getServerSortField(defaultSort.key, defaultSort.direction);

      setLocalStorageSort(paramSort);
      updateSearchParams(paramSort);

      setSorting(defaultSort);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions]);

  const setSort = useCallback(
    (newSort: SortState) => {
      const sortField = getServerSortField(newSort.key, newSort.direction);
      updateSearchParams(sortField);
      setLocalStorageSort(sortField);
      setSorting(newSort);
    },
    [setSorting, setLocalStorageSort],
  );

  const serverSortField = useMemo(() => getServerSortField(sort.key, sort.direction), [sort]);

  return { sort, setSort, serverSortField };
};
